import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Check, HandCoins, Undo2 } from 'lucide-react';
import { bcPaymentsService, specialEventsService, eventsService } from '../../services';
import { LoadingPage } from '../../components/ui/Loading';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { formatGold } from '../../utils/goldNotation';

const ROLE_LABELS = { ATAQUE: 'Ataque', BUFF: 'Buff', SUPORTE: 'Suporte' };
const PROOF_LABELS = {
  APROVADO: { text: 'Prova aprovada', className: 'bg-green-500/20 text-green-400 border-green-500/30' },
  AGUARDANDO_APROVACAO: { text: 'Aguardando aprovação', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  SEM_PROVA: { text: 'Sem prova', className: 'bg-red-500/20 text-red-400 border-red-500/30' },
  NAO_APLICAVEL: { text: 'Não aplicável', className: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
};
const PROOF_BLOCKS_PAYMENT = ['SEM_PROVA', 'AGUARDANDO_APROVACAO'];

const BcPaymentPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [specialEvent, setSpecialEvent] = useState(null);
  const [payment, setPayment] = useState(null);
  const [events, setEvents] = useState([]);

  const [prizeInput, setPrizeInput] = useState('');
  const [linkedEventId, setLinkedEventId] = useState('');
  const [saving, setSaving] = useState(false);

  const [manualForm, setManualForm] = useState({ characterName: '', recipientName: '', role: 'ATAQUE' });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const [eventData, eventsList] = await Promise.all([
        specialEventsService.getById(id),
        eventsService.getEvents({ limit: 200 }),
      ]);
      setSpecialEvent(eventData.event || eventData);
      setEvents(eventsList.events || eventsList || []);

      try {
        const existingPayment = await bcPaymentsService.getBySpecialEvent(id);
        setPayment(existingPayment);
        setLinkedEventId(existingPayment.linkedEventId);
        setPrizeInput(String(existingPayment.prizeAmount));
      } catch (err) {
        if (err.response?.status !== 404) {
          throw err;
        }
        setPayment(null);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao carregar dados do pagamento BC.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      const created = await bcPaymentsService.generate(id, { prizeAmount: prizeInput, linkedEventId });
      setPayment(created);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao gerar pagamento BC.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePrize = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      const updated = await bcPaymentsService.update(payment.id, { prizeAmount: prizeInput, linkedEventId });
      setPayment(updated);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao atualizar pagamento BC.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddManual = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      const updated = await bcPaymentsService.addBeneficiary(payment.id, manualForm);
      setPayment(updated);
      setManualForm({ characterName: '', recipientName: '', role: 'ATAQUE' });
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao incluir beneficiário.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleExcluded = async (beneficiary) => {
    try {
      setError('');
      const updated = await bcPaymentsService.toggleExcluded(payment.id, beneficiary.id, !beneficiary.excluded);
      setPayment(updated);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao atualizar beneficiário.');
    }
  };

  const handleRemoveManual = async (beneficiary) => {
    try {
      setError('');
      const updated = await bcPaymentsService.removeBeneficiary(payment.id, beneficiary.id);
      setPayment(updated);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao remover beneficiário.');
    }
  };

  const handlePay = async (beneficiary) => {
    try {
      setError('');
      const updated = await bcPaymentsService.payBeneficiary(payment.id, beneficiary.id);
      setPayment(updated);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao marcar como pago.');
    }
  };

  const handleDonate = async (beneficiary) => {
    try {
      setError('');
      const updated = await bcPaymentsService.donateBeneficiary(payment.id, beneficiary.id);
      setPayment(updated);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao registrar doação.');
    }
  };

  const handleUndo = async (beneficiary) => {
    try {
      setError('');
      const updated = await bcPaymentsService.undoBeneficiary(payment.id, beneficiary.id);
      setPayment(updated);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao desfazer.');
    }
  };

  if (loading) return <LoadingPage />;

  const isLocked = payment?.beneficiaries?.some((b) => b.status !== 'PENDING');

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(`/app/admin/special-events/${id}`)}
            className="p-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 transition-all"
          >
            <ArrowLeft className="w-5 h-5 text-orange-400" />
          </button>
          <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">
            💰 Pagamento BC — {specialEvent?.name}
          </h1>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/50 rounded-xl p-4">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {!payment ? (
        <form
          onSubmit={handleGenerate}
          className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl space-y-4"
        >
          <h2 className="text-xl font-bold text-white">Gerar relatório de pagamento</h2>
          <Input
            label="Valor da premiação (gold)"
            placeholder="ex: 1.5kk"
            value={prizeInput}
            onChange={(e) => setPrizeInput(e.target.value)}
            variant="dark"
            required
          />
          <div>
            <label className="block text-sm font-semibold mb-2 text-white">Evento normal vinculado</label>
            <Select value={linkedEventId} onChange={(e) => setLinkedEventId(e.target.value)} required>
              <option value="">Selecione um evento</option>
              {events.map((ev) => (
                <option key={ev.id} value={ev.id}>{ev.name}</option>
              ))}
            </Select>
          </div>
          <Button type="submit" loading={saving}>Gerar relatório</Button>
        </form>
      ) : (
        <>
          <form
            onSubmit={handleUpdatePrize}
            className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl space-y-4"
          >
            <h2 className="text-xl font-bold text-white">Premiação</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Input
                label="Valor total"
                value={prizeInput}
                onChange={(e) => setPrizeInput(e.target.value)}
                disabled={isLocked}
                variant="dark"
              />
              <div>
                <label className="block text-sm font-semibold mb-2 text-white">Evento vinculado</label>
                <Select value={linkedEventId} onChange={(e) => setLinkedEventId(e.target.value)} disabled={isLocked}>
                  {events.map((ev) => (
                    <option key={ev.id} value={ev.id}>{ev.name}</option>
                  ))}
                </Select>
              </div>
              <div className="flex items-end">
                {!isLocked && <Button type="submit" loading={saving}>Salvar</Button>}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4 text-center pt-4 border-t border-white/10">
              <div>
                <p className="text-gray-400 text-xs">Custo administrativo (30%)</p>
                <p className="text-white font-bold" title={Number(payment.adminCostAmount).toLocaleString('pt-BR')}>{formatGold(payment.adminCostAmount)}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs">Caixa do clube (20%)</p>
                <p className="text-white font-bold" title={Number(payment.treasuryAmount).toLocaleString('pt-BR')}>{formatGold(payment.treasuryAmount)}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs">Pilotos (50%)</p>
                <p className="text-white font-bold" title={Number(payment.pilotsShareAmount).toLocaleString('pt-BR')}>{formatGold(payment.pilotsShareAmount)}</p>
              </div>
            </div>
          </form>

          {['ATAQUE', 'BUFF', 'SUPORTE'].map((role) => {
            const rows = (payment.beneficiaries || []).filter((b) => b.role === role);
            if (rows.length === 0) return null;

            return (
              <div key={role} className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
                <h2 className="text-xl font-bold text-white mb-4">{ROLE_LABELS[role]} ({rows.length})</h2>
                <div className="space-y-2">
                  {rows.map((beneficiary) => {
                    const proof = PROOF_LABELS[beneficiary.proofStatus] || PROOF_LABELS.NAO_APLICAVEL;
                    return (
                      <div
                        key={beneficiary.id}
                        className={`flex flex-wrap items-center justify-between gap-3 p-3 rounded-xl border ${
                          beneficiary.excluded ? 'border-white/5 bg-white/5 opacity-50' : 'border-white/10 bg-white/5'
                        }`}
                      >
                        <div className="min-w-[180px]">
                          <p className="text-white font-medium">{beneficiary.characterName}</p>
                          <p className="text-gray-400 text-sm">Beneficiário: {beneficiary.recipientName}</p>
                        </div>
                        <span className={`px-2 py-1 rounded-lg text-xs border ${proof.className}`}>{proof.text}</span>
                        <p className="text-white font-bold" title={Number(beneficiary.shareAmount).toLocaleString('pt-BR')}>{formatGold(beneficiary.shareAmount)}</p>
                        <div className="flex gap-2 flex-wrap">
                          {beneficiary.status === 'PENDING' && !beneficiary.excluded && (
                            <>
                              <Button
                                size="sm"
                                variant="success"
                                disabled={PROOF_BLOCKS_PAYMENT.includes(beneficiary.proofStatus)}
                                onClick={() => handlePay(beneficiary)}
                              >
                                <Check className="w-4 h-4" /> Pagar
                              </Button>
                              <Button size="sm" variant="secondary" onClick={() => handleDonate(beneficiary)}>
                                <HandCoins className="w-4 h-4" /> Doar
                              </Button>
                            </>
                          )}
                          {beneficiary.status !== 'PENDING' && (
                            <>
                              <span className="text-sm text-gray-300">
                                {beneficiary.status === 'PAID' ? 'Pago' : 'Doado'}
                              </span>
                              <Button size="sm" variant="ghost" onClick={() => handleUndo(beneficiary)}>
                                <Undo2 className="w-4 h-4" /> Desfazer
                              </Button>
                            </>
                          )}
                          {beneficiary.status === 'PENDING' && beneficiary.source === 'AUTO' && (
                            <Button size="sm" variant="outline" onClick={() => handleToggleExcluded(beneficiary)}>
                              {beneficiary.excluded ? 'Reincluir' : 'Excluir'}
                            </Button>
                          )}
                          {beneficiary.status === 'PENDING' && beneficiary.source === 'MANUAL' && (
                            <Button size="sm" variant="danger" onClick={() => handleRemoveManual(beneficiary)}>
                              Remover
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          <form
            onSubmit={handleAddManual}
            className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl space-y-4" hidden={true}
          >
            <h2 className="text-xl font-bold text-white flex items-center gap-2"><Plus className="w-5 h-5" /> Incluir beneficiário manualmente</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Input
                label="Nome do personagem"
                value={manualForm.characterName}
                onChange={(e) => setManualForm({ ...manualForm, characterName: e.target.value })}
                variant="dark"
                required
              />
              <Input
                label="Nome do beneficiário (piloto/dono)"
                value={manualForm.recipientName}
                onChange={(e) => setManualForm({ ...manualForm, recipientName: e.target.value })}
                variant="dark"
                required
              />
              <div>
                <label className="block text-sm font-semibold mb-2 text-white">Papel</label>
                <Select value={manualForm.role} onChange={(e) => setManualForm({ ...manualForm, role: e.target.value })}>
                  <option value="ATAQUE">Ataque</option>
                  <option value="BUFF">Buff</option>
                  <option value="SUPORTE">Suporte</option>
                </Select>
              </div>
            </div>
            <Button type="submit" loading={saving}>Incluir</Button>
          </form>
        </>
      )}
    </div>
  );
};

export default BcPaymentPage;
